import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { otpRequest, otpVerify, telegramLogin } from '../../api/auth';
import { OtpInput } from '../../components/OtpInput';
import { TelegramLoginButton } from '../../components/TelegramLoginButton';
import { BackIcon } from '../../components/icons';
import { LegalDocOverlay, type LegalDoc } from '../../components/LegalDocOverlay';
import { MascotWatermark } from '../../components/Watermark';
import { haptics } from '../../telegram';

const RESEND_SECONDS = 60;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function LoginScreen() {
  const { loginWithToken } = useAuth();
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [legalDoc, setLegalDoc] = useState<LegalDoc | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const doRequest = async (): Promise<boolean> => {
    const e = email.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) {
      setError('Введите корректную почту');
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await otpRequest(e);
      setDevCode(res.devCode);
      setCooldown(RESEND_SECONDS);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить код');
      haptics.error();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const sendCode = async () => {
    if (await doRequest()) {
      setStep('otp');
      setCode('');
      haptics.tap();
    }
  };

  const resend = async () => {
    if (cooldown <= 0) await doRequest();
  };

  const verify = async () => {
    if (code.length !== 6) {
      setError('Введите 6-значный код');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await otpVerify(email.trim().toLowerCase(), code);
      haptics.success();
      loginWithToken(res.token, res.player); // unmounts this screen
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неверный код');
      haptics.error();
      setBusy(false);
    }
  };

  const onTelegram = async (user: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await telegramLogin(user);
      loginWithToken(res.token, res.player);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка входа через Telegram');
      setBusy(false);
    }
  };

  const mmss = `${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, '0')}`;

  if (legalDoc) {
    return <LegalDocOverlay doc={legalDoc} onClose={() => setLegalDoc(null)} />;
  }

  if (step === 'otp') {
    return (
      <div className="auth auth--brand">
        <MascotWatermark pos="otp" />
        <button
          className="hbtn auth-back"
          onClick={() => {
            setStep('email');
            setError(null);
          }}
          aria-label="Назад"
        >
          <BackIcon />
        </button>
        <div className="auth-title">Введите код</div>
        <div className="auth-sub">
          Код отправлен на <span className="auth-email">{email.trim().toLowerCase()}</span>{' '}
          <button
            className="auth-edit"
            onClick={() => {
              setStep('email');
              setError(null);
            }}
          >
            изменить
          </button>
        </div>

        {devCode && <div className="auth-devcode">DEV-код: {devCode}</div>}

        <OtpInput value={code} onChange={setCode} disabled={busy} />

        {error && <div className="field-error auth-error">{error}</div>}

        <div className="otp-resend">
          {cooldown > 0 ? (
            <>Отправить повторно через <span className="mono auth-timer">{mmss}</span></>
          ) : (
            <button className="auth-link" onClick={resend} disabled={busy}>
              Отправить повторно
            </button>
          )}
        </div>

        <button className="btn btn--inverted" onClick={verify} disabled={busy || code.length !== 6}>
          {busy ? 'Проверяем…' : 'Подтвердить'}
        </button>
      </div>
    );
  }

  return (
    <div className="auth auth--brand">
      <MascotWatermark pos="center" />
      <img className="auth-logo" src="/ante-white.svg" alt="ANTÉ" />
      <div className="auth-title">Вход в клуб</div>
      <div className="auth-sub">Укажите почту — пришлём одноразовый код. Пароль не нужен.</div>

      <div className="auth-field-label">Почта</div>
      <div className="auth-field">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          onKeyDown={(e) => e.key === 'Enter' && sendCode()}
        />
      </div>

      {error && <div className="field-error auth-error">{error}</div>}

      <button className="btn btn--inverted" onClick={sendCode} disabled={busy}>
        {busy ? 'Отправляем…' : 'Отправить код'}
      </button>

      <div className="auth-divider">
        <span />
        <em>или</em>
        <span />
      </div>

      <TelegramLoginButton onAuth={onTelegram} />

      <div className="auth-footer">
        Продолжая, вы соглашаетесь с{' '}
        <button className="auth-link-text" onClick={() => setLegalDoc('offer')}>
          офертой
        </button>{' '}
        и{' '}
        <button className="auth-link-text" onClick={() => setLegalDoc('consent')}>
          согласием на обработку персональных данных
        </button>
      </div>
    </div>
  );
}
